import Interaction from "../../../models/interactions/Interaction";
import { ILookat, ILookatConfig } from "../../../models/interactions/Lookat";
import PiButton from "../../../components/PiButton";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import LookatEntryCard from "./LookatEntryCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/Store";
import { selectErrorsForInteraction } from "../../../app/slices/ValidationSlice";
import {
  selectSelectedElement,
  selectSelectedTake3D,
} from "../../../app/slices/TrainingSlice";
import { ISequence } from "../../../models/interactions/Sequence";
import { getActorPositionIdInTake } from "../EnvironmentPositionContext";

export default function LookatNodePreview(props: {
  nodeId: string;
  interaction: Interaction;
}) {
  const interaction = props.interaction as ILookat;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const take = useSelector(selectSelectedTake3D);
  const selectedElement = useSelector(selectSelectedElement) as
    | ISequence
    | undefined;
  const sequenceErrors = useSelector((state: RootState) =>
    selectedElement?.id ? selectErrorsForInteraction(state, selectedElement.id) : [],
  );
  const hasNodeErrors = sequenceErrors.some(
    (error) => typeof error !== "string" && error.nodeId === props.nodeId,
  );

  const updateLookatAt = (index: number, value: {
    sourceNpcId?: string;
    targetId?: string;
    finalIK?: boolean;
  }) => {
    const lookats = interaction.lookats.length
      ? [...interaction.lookats]
      : [{ sourceNpcId: "", targetId: "" }];

    lookats[index] = {
      ...(lookats[index] ?? { sourceNpcId: "", targetId: "" }),
      ...value,
    };

    // An actor cannot look at the position it occupies itself.
    if (value.sourceNpcId !== undefined) {
      const ownPositionId = getActorPositionIdInTake(take, value.sourceNpcId);
      if (ownPositionId && lookats[index].targetId === ownPositionId) {
        lookats[index] = { ...lookats[index], targetId: "" };
      }
    }

    updateInteraction(props.nodeId, {
      ...interaction,
      lookats,
    } as ILookat);
  };

  const addLookat = () => {
    updateInteraction(props.nodeId, {
      ...interaction,
      lookats: [...interaction.lookats, { sourceNpcId: "", targetId: "" }],
    } as ILookat);
  };

  const deleteLookat = (index: number) => {
    const lookats = interaction.lookats.filter((_, i) => i !== index);

    updateInteraction(props.nodeId, {
      ...interaction,
      lookats: lookats.length ? lookats : [{ sourceNpcId: "", targetId: "" }],
    } as ILookat);
  };

  const lookatsToRender: ILookatConfig[] = interaction.lookats.length
    ? interaction.lookats
    : [{ sourceNpcId: "", targetId: "" }];

  return (
    <div
      className="w-full h-full flex flex-col gap-[12px] pb-[24px]"
      onWheelCapture={(e) => e.stopPropagation()}
    >
      <div className="w-full flex flex-col gap-[12px] max-h-[320px] overflow-y-auto pit-creator-scrollbar pr-[4px]">
        {lookatsToRender.map((lookat, index) => (
          <LookatEntryCard
            key={index}
            nodeId={props.nodeId}
            index={index}
            total={lookatsToRender.length}
            hasErrors={hasNodeErrors}
            sourceNpcId={lookat.sourceNpcId}
            targetId={lookat.targetId}
            finalIK={lookat.finalIK}
            onActorChange={(sourceNpcId) => updateLookatAt(index, { sourceNpcId })}
            onTargetChange={(targetId) => updateLookatAt(index, { targetId })}
            onLookModeChange={(finalIK) => updateLookatAt(index, { finalIK })}
            onDelete={() => deleteLookat(index)}
          />
        ))}
      </div>
      <div className="h-[36px]">
        <PiButton variant="piBlueOutlined" onClick={addLookat}>
          + Add Lookat
        </PiButton>
      </div>
    </div>
  );
}
